import type { QueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";

import { Spinner } from "@performyard/py-components";
import type { SideNavTheme } from "@performyard/service-contracts";

import { AddCustomThemeButton } from "./components/AddCustomThemeButton";
import { ThemeEditorCard } from "./components/ThemeEditorCard";
import { ThemeRadioList } from "./components/ThemeRadioList";

import {
	useCreateSideNavTheme,
	useDeleteSideNavTheme,
	useSideNavThemes,
	useUpdateSideNavTheme,
} from "../../api/hooks/defs/side-nav-themes";

import { NAVIGATION_CONFIG_QUERY_KEY } from "../../api/queries/defs/navigation";
import { queryClient } from "../../api/queryClient";

type EditorState = { mode: "list" } | { mode: "create" } | { mode: "edit"; themeId: string };

const NAVIGATION_CONFIG_KEYS = [[NAVIGATION_CONFIG_QUERY_KEY, "self"], [NAVIGATION_CONFIG_QUERY_KEY]] as const;

/** Drop any optimistic preview by refetching the persisted navigation config. */
export const clearNavigationConfigPreview = (client: QueryClient) => {
	NAVIGATION_CONFIG_KEYS.forEach((key) => client.invalidateQueries({ queryKey: key }));
};

/** Optimistic patch so the SideNav previews the theme without persisting it. */
export const patchNavigationConfigThemePreview = (queryClient: QueryClient, previewTheme: SideNavTheme | undefined) => {
	NAVIGATION_CONFIG_KEYS.forEach((key) => {
		queryClient.setQueriesData<{ navigationConfig: { sideNavTheme?: SideNavTheme } }>({ queryKey: key }, (prev) => {
			if (!prev?.navigationConfig) return prev;
			return {
				...prev,
				navigationConfig: { ...prev.navigationConfig, sideNavTheme: previewTheme },
			};
		});
	});
};

type Props = {
	companyId?: string;
	value?: string;
	onChange?: (themeId: string) => void;
	onPersistedValueChange?: (themeId: string) => void;
	canEdit?: boolean;
	disabled?: boolean;
};

const SideNavThemePicker = ({
	companyId,
	value,
	onChange = () => undefined,
	onPersistedValueChange,
	canEdit = true,
	disabled = false,
}: Props) => {
	const { data: themesData, isLoading } = useSideNavThemes(companyId);

	const createMutation = useCreateSideNavTheme(companyId);
	const updateMutation = useUpdateSideNavTheme(companyId);
	const deleteMutation = useDeleteSideNavTheme(companyId);

	const [editor, setEditor] = useState<EditorState>({ mode: "list" });

	// On unmount, clear any optimistic preview by re-fetching.
	useEffect(() => () => clearNavigationConfigPreview(queryClient), []);

	const themes: SideNavTheme[] = useMemo(() => themesData?.themes ?? [], [themesData?.themes]);
	const customThemes = useMemo(() => themes.filter((t) => !t.isDefault), [themes]);
	const persistedSelectedThemeId = themesData?.selectedThemeId ?? null;
	const effectiveSelectedThemeId = value ?? persistedSelectedThemeId;
	const editingTheme = editor.mode === "edit" ? customThemes.find((t) => t.id === editor.themeId) : undefined;

	useEffect(() => {
		if (persistedSelectedThemeId) onPersistedValueChange?.(persistedSelectedThemeId);
	}, [onPersistedValueChange, persistedSelectedThemeId]);

	const isMutationInFlight = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

	if (isLoading || !themesData) {
		return (
			<div className="flex items-center justify-center py-6">
				<Spinner size={32} />
			</div>
		);
	}

	const handleSelect = (themeId: string) => {
		if (!canEdit) return;
		onChange(themeId);
		const previewTheme = themes.find((t) => t.id === themeId);
		patchNavigationConfigThemePreview(queryClient, previewTheme);
	};

	const handleAddTheme = (backgroundColor: string) => {
		createMutation.mutate(
			{ backgroundColor },
			{
				onSuccess: () => {
					setEditor({ mode: "list" });
				},
			}
		);
	};

	const handleUpdateTheme = (backgroundColor: string) => {
		if (editor.mode !== "edit") return;
		updateMutation.mutate(
			{ themeId: editor.themeId, data: { backgroundColor } },
			{
				onSuccess: () => {
					setEditor({ mode: "list" });
				},
			}
		);
	};

	const deleteTheme = (themeId: string) => {
		deleteMutation.mutate(themeId, {
			onSuccess: (result) => {
				if (effectiveSelectedThemeId === themeId) {
					onChange(result.selectedThemeId);
					patchNavigationConfigThemePreview(queryClient, undefined);
				}
				setEditor({ mode: "list" });
			},
		});
	};

	return (
		<div className="flex flex-col gap-4">
			<ThemeRadioList
				themes={themes}
				selectedThemeId={effectiveSelectedThemeId ?? ""}
				onSelect={handleSelect}
				onEdit={(themeId) => setEditor({ mode: "edit", themeId })}
				onDelete={deleteTheme}
				canEdit={canEdit}
				disabled={disabled || isMutationInFlight || !canEdit}
			/>

			{canEdit ? (
				<div>
					{editor.mode === "create" ? (
						<ThemeEditorCard
							mode="create"
							title="Custom Theme"
							initialBackgroundColor="#FFFFFF"
							isSubmitting={createMutation.isPending}
							onSubmit={handleAddTheme}
							onCancel={() => setEditor({ mode: "list" })}
						/>
					) : editor.mode === "edit" && editingTheme ? (
						// `key` forces remount on edit-target switch so initialBackgroundColor refreshes.
						<ThemeEditorCard
							key={editor.themeId}
							mode="edit"
							title={editingTheme.name}
							initialBackgroundColor={editingTheme.backgroundColor}
							isSubmitting={updateMutation.isPending}
							onSubmit={handleUpdateTheme}
							onCancel={() => setEditor({ mode: "list" })}
						/>
					) : customThemes.length >= themesData.maxCustomThemes ? null : (
						<AddCustomThemeButton onClick={() => setEditor({ mode: "create" })} disabled={disabled} />
					)}
				</div>
			) : null}
		</div>
	);
};

export default SideNavThemePicker;
